import { useState, useEffect } from 'react';
import { tareasAPI, colmenasAPI, zonasAPI } from '../api/client';
import { useTranslation } from 'react-i18next';

const PRIORIDAD_COLOR = { alta: '#C0392B', normal: '#E8922A', baja: '#8B6355' };
const PRIORIDAD_BG = { alta: '#FDECEA', normal: '#FDF0DC', baja: '#F0EDE9' };

export default function Tareas() {
  const { t } = useTranslation();
  const [tareas, setTareas] = useState([]);
  const [colmenas, setColmenas] = useState([]);
  const [zonas, setZonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('pendiente');
  const [modal, setModal] = useState(false);

  const load = () =>
    Promise.all([
      tareasAPI.list(filtro !== 'todas' ? { estado: filtro } : {}),
      colmenasAPI.list(),
      zonasAPI.list(),
    ]).then(([tsk, c, z]) => { setTareas(tsk.data); setColmenas(c.data); setZonas(z.data); })
    .finally(() => setLoading(false));

  useEffect(() => { load(); }, [filtro]);

  const completar = async (id) => { await tareasAPI.completar(id); load(); };
  const omitir = async (id) => { await tareasAPI.omitir(id); load(); };
  const eliminar = async (id) => { await tareasAPI.delete(id); load(); };

  const pendientes = tareas.filter(tsk => tsk.estado === 'pendiente').length;

  const filters = [
    ['pendiente', t('tareas.filterPending')],
    ['hecho', t('tareas.filterDone')],
    ['omitida', t('tareas.filterSkipped')],
    ['todas', t('tareas.filterAll')],
  ];

  return (
    <div>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>{t('tareas.title')}</h1>
          <p style={S.sub}>{pendientes > 0 ? t('tareas.pendingSub', { count: pendientes, s: pendientes !== 1 ? 's' : '' }) : t('tareas.upToDate')}</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal(true)}>{t('tareas.newBtn')}</button>
      </div>

      <div style={S.tabs}>
        {filters.map(([v, l]) => (
          <button key={v} onClick={() => setFiltro(v)}
            style={{ ...S.tab, ...(filtro === v ? S.tabActive : {}) }}>{l}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#8B6355' }}>{t('common.loading')}</div>
      ) : tareas.length === 0 ? (
        <div className="empty-state">
          <div className="icon">📋</div>
          <h3>{filtro === 'pendiente' ? t('tareas.emptyPending') : t('tareas.emptyOther')}</h3>
          <p>{filtro === 'pendiente' ? t('tareas.emptyPendingMsg') : t('tareas.emptyOtherMsg')}</p>
          {filtro === 'pendiente' && <button className="btn btn-primary" onClick={() => setModal(true)}>{t('tareas.newBtn')}</button>}
        </div>
      ) : (
        <div style={S.lista}>
          {tareas.map(tsk => (
            <TareaCard key={tsk.id} tarea={tsk}
              onCompletar={() => completar(tsk.id)}
              onOmitir={() => omitir(tsk.id)}
              onEliminar={() => eliminar(tsk.id)} />
          ))}
        </div>
      )}

      {modal && (
        <TareaModal colmenas={colmenas} zonas={zonas}
          onSave={async (data) => { await tareasAPI.create(data); setModal(false); load(); }}
          onClose={() => setModal(false)} />
      )}
    </div>
  );
}

function TareaCard({ tarea: tsk, onCompletar, onOmitir, onEliminar }) {
  const { t } = useTranslation();
  const hecho = tsk.estado === 'hecho';
  const omitida = tsk.estado === 'omitida';
  const prioridadLabel = { alta: t('tareas.priorityHigh'), normal: t('tareas.priorityNormal'), baja: t('tareas.priorityLow') };

  return (
    <div style={{ ...S.tareaCard, opacity: hecho || omitida ? 0.65 : 1 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        {!hecho && !omitida && (
          <button onClick={onCompletar} style={S.checkCircle} title={t('tareas.complete')}>
            <span style={{ fontSize: 11, color: '#8B6355' }}>✓</span>
          </button>
        )}
        {hecho && <div style={{ ...S.checkCircle, background: '#4A7C59', border: '2px solid #4A7C59' }}><span style={{ fontSize: 11, color: 'white' }}>✓</span></div>}
        {omitida && <div style={{ ...S.checkCircle, background: '#F0EDE9', border: '2px solid #D0C8C0' }}><span style={{ fontSize: 11, color: '#8B6355' }}>—</span></div>}

        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 500, textDecoration: hecho || omitida ? 'line-through' : 'none', color: hecho || omitida ? '#8B6355' : '#2C1A0E' }}>
            {tsk.descripcion}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ ...S.prioridadBadge, background: PRIORIDAD_BG[tsk.prioridad], color: PRIORIDAD_COLOR[tsk.prioridad] }}>
              {prioridadLabel[tsk.prioridad] || tsk.prioridad}
            </span>
            {tsk.colmena_nombre && <span className="badge badge-amber">🐝 {tsk.colmena_nombre}</span>}
            {tsk.zona_nombre && !tsk.colmena_nombre && <span className="badge" style={{ background: '#F0EDE9', color: '#5C3D2E' }}>📍 {tsk.zona_nombre}</span>}
            {!tsk.colmena_nombre && !tsk.zona_nombre && <span className="badge badge-gray">📌 {t('tareas.generalBadge')}</span>}
            {tsk.fecha_limite && (
              <span style={{ fontSize: 11, color: '#8B6355' }}>
                📅 {new Date(tsk.fecha_limite).toLocaleDateString(undefined, { day: '2-digit', month: 'short' })}
              </span>
            )}
          </div>
          {tsk.notas && <div style={{ fontSize: 12, color: '#8B6355', marginTop: 6, fontStyle: 'italic' }}>{tsk.notas}</div>}
        </div>

        {!hecho && !omitida && (
          <div style={{ display: 'flex', gap: 4 }}>
            <button onClick={onOmitir} style={S.accionBtn} title={t('tareas.skip')}>—</button>
            <button onClick={onEliminar} style={{ ...S.accionBtn, color: '#C0392B' }} title={t('tareas.deleteTask')}>✕</button>
          </div>
        )}
      </div>
    </div>
  );
}

function TareaModal({ colmenas, zonas, onSave, onClose }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    descripcion: '', prioridad: 'normal', colmena_id: '', zona_id: '', notas: '', fecha_limite: '', alcance: 'general',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async e => {
    e.preventDefault(); setSaving(true);
    try {
      await onSave({
        descripcion: form.descripcion,
        prioridad: form.prioridad,
        colmena_id: form.alcance === 'colmena' && form.colmena_id ? parseInt(form.colmena_id) : null,
        zona_id: form.alcance === 'zona' && form.zona_id ? parseInt(form.zona_id) : null,
        notas: form.notas || null,
        fecha_limite: form.fecha_limite || null,
      });
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h2>{t('tareas.modalTitle')}</h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">{t('tareas.descLabel')}</label>
              <input value={form.descripcion} onChange={e => set('descripcion', e.target.value)}
                className="form-input" placeholder={t('tareas.descPlaceholder')} required />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">{t('tareas.priorityLabel')}</label>
                <select value={form.prioridad} onChange={e => set('prioridad', e.target.value)} className="form-input">
                  <option value="baja">{t('tareas.priorityLow')}</option>
                  <option value="normal">{t('tareas.priorityNormal')}</option>
                  <option value="alta">{t('tareas.priorityHigh')}</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{t('tareas.dueDateModalLabel')}</label>
                <input type="date" value={form.fecha_limite} onChange={e => set('fecha_limite', e.target.value)} className="form-input" />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{t('tareas.assignTo')}</label>
              <select value={form.alcance} onChange={e => set('alcance', e.target.value)} className="form-input">
                <option value="general">{t('tareas.scopeGeneral')}</option>
                <option value="colmena">{t('tareas.scopeHive')}</option>
                <option value="zona">{t('tareas.scopeZone')}</option>
              </select>
            </div>

            {form.alcance === 'colmena' && (
              <div className="form-group">
                <label className="form-label">{t('tareas.colmenaLabel')}</label>
                <select value={form.colmena_id} onChange={e => set('colmena_id', e.target.value)} className="form-input">
                  <option value="">{t('tareas.selectPlaceholder')}</option>
                  {colmenas.filter(c => c.activa).map(c => <option key={c.id} value={c.id}>{c.nombre} — {c.zona_nombre}</option>)}
                </select>
              </div>
            )}

            {form.alcance === 'zona' && (
              <div className="form-group">
                <label className="form-label">{t('tareas.zonaLabel')}</label>
                <select value={form.zona_id} onChange={e => set('zona_id', e.target.value)} className="form-input">
                  <option value="">{t('tareas.selectPlaceholder')}</option>
                  {zonas.map(z => <option key={z.id} value={z.id}>{z.nombre}</option>)}
                </select>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">{t('tareas.notesLabel')}</label>
              <textarea value={form.notas} onChange={e => set('notas', e.target.value)} className="form-input" placeholder={t('tareas.notesPlaceholder')} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? t('common.saving') : t('tareas.createBtn')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const S = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  title: { fontSize: 26, fontFamily: "'DM Sans', sans-serif" },
  sub: { color: '#8B6355', fontSize: 14, marginTop: 4 },
  tabs: { display: 'flex', background: '#F0EDE9', borderRadius: 10, padding: 4, marginBottom: 20, gap: 2, width: 'fit-content' },
  tab: { padding: '7px 16px', borderRadius: 8, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, color: '#8B6355', transition: 'all 0.15s' },
  tabActive: { background: 'white', color: '#2C1A0E', fontWeight: 500, boxShadow: '0 1px 4px rgba(44,26,14,0.1)' },
  lista: { display: 'flex', flexDirection: 'column', gap: 8 },
  tareaCard: { background: 'white', borderRadius: 12, padding: '14px 16px', boxShadow: '0 1px 6px rgba(44,26,14,0.08)', border: '1px solid rgba(232,146,42,0.10)', transition: 'box-shadow 0.2s' },
  checkCircle: { width: 24, height: 24, borderRadius: '50%', border: '2px solid #FAE0B0', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2, transition: 'all 0.15s' },
  prioridadBadge: { padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, textTransform: 'capitalize' },
  accionBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#8B6355', fontSize: 13, padding: '4px 6px', borderRadius: 6, transition: 'background 0.15s' },
};
